import React from 'react';
import _ from 'lodash';

import LivePage from './LivePage';
import submenuSecurity from './menus/submenu-security';
import SingleValueSelector from '../components/common/editors/SingleValueSelector';
import MultiButtonSwitch from '../components/common/editors/MultiButtonSwitch';
import FiltersBar from '../components/common/filters-bar/FiltersBar';
import { LocalesSelector } from '../components/lego/LocalesSelector';
import { stringToColor } from '../components/common/colorUtils';
import AIModelSelector from './cerokm/components/AIModelSelector';
import CountryFlag from '../components/common/CountryFlag';

const LOCALES = ['es', 'en', 'pt'];
const SITE_DOMAIN_BY_LOCALE = { es: 'www.opinautos.com', en: 'www.startmycar.com', pt: 'www.opinautos.com.br' };
const DEFAULT_AI_MODEL = 'gemini-flash-latest';
const WINDOW_OPTIONS = [
  { value: 5, label: '5m' },
  { value: 15, label: '15m' },
  { value: 60, label: '1h' },
  { value: 180, label: '3h' },
  { value: 360, label: '6h' },
  { value: 720, label: '12h' },
  { value: 1440, label: '1d' },
];
const DEFAULT_WINDOW_MINUTES = 60;

function CountryLabel({ code }) {
  if (!code) return <span className='text-muted'>—</span>;
  return <span className='text-nowrap'><CountryFlag countryCode={code.toLowerCase()} small /> {code}</span>;
}

function prefix2ToWildcard(prefix2) {
  const n = parseInt(prefix2, 10);
  if (Number.isNaN(n)) return '?.?.*.*';
  return `${(n >>> 8) & 0xff}.${n & 0xff}.*.*`;
}

// Parse an IPv4 CIDR like "192.168.1.0/24" into an unsigned int [start, end] range.
// Returns null for IPv6 (skipped) or unparseable input.
function parseIpv4Cidr(cidr) {
  if (!cidr || cidr.indexOf(':') !== -1) return null;
  const [ip, lenStr] = cidr.split('/');
  const len = parseInt(lenStr, 10);
  if (!ip || Number.isNaN(len) || len < 0 || len > 32) return null;
  const parts = ip.split('.').map(p => parseInt(p, 10));
  if (parts.length !== 4 || parts.some(p => Number.isNaN(p) || p < 0 || p > 255)) return null;
  const ipInt = ((parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3]) >>> 0;
  const mask = len === 0 ? 0 : ((0xFFFFFFFF << (32 - len)) >>> 0);
  const start = (ipInt & mask) >>> 0;
  const end = (start | ((~mask) >>> 0)) >>> 0;
  return { start, end, cidr };
}

function buildKnownGoodIndex(knownGoodCidrs) {
  return (knownGoodCidrs || [])
    .map(({ name, cidr }) => {
      const r = parseIpv4Cidr(cidr);
      return r ? { name, ...r } : null;
    })
    .filter(Boolean);
}

function findKnownGoodOverlap(parsedCidrs, rangeStart, rangeEnd) {
  for (const c of parsedCidrs) {
    if (rangeStart <= c.end && c.start <= rangeEnd) return c;
  }
  return null;
}

function matchPrefix2(parsedCidrs, prefix2) {
  const n = parseInt(prefix2, 10);
  if (Number.isNaN(n)) return null;
  const start = (n << 16) >>> 0;
  const end = (start | 0xFFFF) >>> 0;
  return findKnownGoodOverlap(parsedCidrs, start, end);
}

function matchIp(parsedCidrs, ipInt) {
  const n = parseInt(ipInt, 10);
  if (Number.isNaN(n)) return null;
  const u = n >>> 0;
  return findKnownGoodOverlap(parsedCidrs, u, u);
}

// "1.2.3.4" → unsigned int, or null if not a valid IPv4
function ipStrToInt(ip) {
  if (!ip) return null;
  const parts = String(ip).trim().split('.').map(p => parseInt(p, 10));
  if (parts.length !== 4 || parts.some(p => Number.isNaN(p) || p < 0 || p > 255)) return null;
  return ((parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3]) >>> 0;
}

function matchIpRangeStr(parsedCidrs, ipFrom, ipTo) {
  const from = ipStrToInt(ipFrom);
  const to = ipStrToInt(ipTo || ipFrom);
  if (from == null || to == null) return null;
  return findKnownGoodOverlap(parsedCidrs, from, to);
}

function buildServerApplyUrl(locale, addBlacklistedIps, addRestrictedIpRanges) {
  const domain = SITE_DOMAIN_BY_LOCALE[locale] || SITE_DOMAIN_BY_LOCALE.es;
  const draft = {
    blacklistedIps: (addBlacklistedIps || []).map(({ ip, description }) => ({ ip, description })),
    restrictedIpRanges: (addRestrictedIpRanges || []).map(({ ipFrom, ipTo, description }) => ({ ipFrom, ipTo, description })),
  };
  return `https://${domain}/admin/blacklisted-ips?draft=${encodeURIComponent(JSON.stringify(draft))}`;
}

const KNOWN_GOOD_TICK_STYLE = `
  .known-good-tick { color: #28a745; font-weight: 600; }
  .known-good-tick::after { content: " ✅"; }
`;

class SecurityTrafficAnalysis extends LivePage {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      locale: this.getUrlParam('locale') || 'es',
      windowMinutes: this.getUrlParam('windowMinutes') || DEFAULT_WINDOW_MINUTES,
      loading: false,
      analyzing: false,
      error: null,
      result: null,
      aiResponse: null,
      aiError: null,
      aiModel: DEFAULT_AI_MODEL,
    };

    this.fullScreen = true;
    this.submenu = submenuSecurity;

    this.trafficSvc = this.service('services/security/traffic-analysis');
    this.aiSvc = this.service('services/openai/prompt');
  }

  setLocale = (locale) => {
    this.setUrlParam('locale', locale);
    this.setState({ locale });
  }

  setWindowMinutes = (windowMinutes) => {
    this.setUrlParam('windowMinutes', windowMinutes);
    this.setState({ windowMinutes });
  }

  analyze = async () => {
    const { locale, windowMinutes } = this.state;
    this.setState({ loading: true, error: null, result: null, aiResponse: null, aiError: null });
    try {
      const result = await this.trafficSvc.find({ query: { locale, windowMinutes } });
      this.setState({ result });
    } catch (err) {
      this.setState({ error: err.message || err.toString() });
    } finally {
      this.setState({ loading: false });
    }
  }

  askAi = async () => {
    const { result } = this.state;
    if (!result) return;
    this.setState({ analyzing: true, aiResponse: null, aiError: null });
    try {
      const res = await this.aiSvc.create({
        systemPrompt: result.systemPrompt,
        userPrompt: result.userPrompt,
        options: { model: this.state.aiModel, json: true, maxTokens: 128000 }
      });
      this.setState({ aiResponse: res });
    } catch (err) {
      this.setState({ aiError: err.message || err.toString() });
    } finally {
      this.setState({ analyzing: false });
    }
  }

  renderPageBody() {
    const { locale, windowMinutes, loading, analyzing, error, result, aiResponse, aiError } = this.state;

    return <div className='margin'>

      <FiltersBar>
        <h4 className='mb-0'>Bot traffic analysis. Locale: <b>{locale}</b></h4>
        <span className='d-inline-flex align-items-center ml-2'>
          <span>
            <LocalesSelector multi={false} value={locale} onChange={l => this.setLocale(l)} />
          </span>

          <span className='ml-2'>
            From last:&nbsp;
            <MultiButtonSwitch value={windowMinutes} options={WINDOW_OPTIONS}
              onChange={(v) => this.setWindowMinutes(v)} />
          </span>

          <button className='btn btn-primary ml-2' onClick={this.analyze} disabled={loading}>
            {loading ? 'Analyzing…' : 'Analyze traffic'}
          </button>
        </span>

      </FiltersBar>

      {error && <div className='alert alert-danger mt-2'>{error}</div>}

      {result && this.renderResult(result, analyzing, aiResponse, aiError)}
    </div>;
  }

  renderResult(result, analyzing, aiResponse, aiError) {
    const styleFromIp = (ip, ipBytes = 4) => {
      const n = parseInt(ip, 10);
      if (Number.isNaN(n)) return {};
      return { minWidth: '150px', backgroundColor: stringToColor((n >>> (8 * (ipBytes - 1))).toString()) + '55' };
    }

    const { date, timeHour, timeMinute, windowMinutes, prefixes, globalIps, drilledPrefixes, ipsByPrefix, ptrByIp, countryByPrefix = {}, knownGoodBotRanges, knownGoodCidrs, elapsedMs } = result;
    const windowLabel = (WINDOW_OPTIONS.find(o => o.value === windowMinutes) || {}).label || `${windowMinutes}m`;
    const anchorIso = timeMinute != null
      ? new Date(timeMinute * 60000).toISOString()
      : new Date(timeHour * 3600000).toISOString();

    const parsedKnownGood = buildKnownGoodIndex(knownGoodCidrs);
    const prefixCellProps = (prefix2) => {
      const m = matchPrefix2(parsedKnownGood, prefix2);
      return {
        className: `monospace${m ? ' known-good-tick' : ''}`,
        title: m ? `Overlaps ${m.name} (${m.cidr})` : undefined
      };
    };
    const ipCellProps = (ipInt) => {
      const m = matchIp(parsedKnownGood, ipInt);
      return {
        className: `monospace${m ? ' known-good-tick' : ''}`,
        title: m ? `Inside ${m.name} (${m.cidr})` : undefined
      };
    };

    return <div className='mt-3 p-2'>    
      <style>{KNOWN_GOOD_TICK_STYLE}</style>
      <div className='small text-muted'>
        Locale <b>{result.locale}</b> • window <b>{windowLabel}</b> ending at <b>{anchorIso}</b> • date <b>{date}</b> • timeHour <b>{timeHour}</b>{timeMinute != null ? <> • timeMinute <b>{timeMinute}</b></> : null} • fetched in {elapsedMs}ms
      </div>

      <div className='mt-3 d-flex align-items-center flex-gap'>
        <button className='btn btn-warning' onClick={this.askAi} disabled={analyzing}>
          {analyzing ? 'Asking AI…' : 'Ask AI for blocklist suggestions'}
        </button>
        <div style={{ width: '300px' }}>
          <AIModelSelector value={this.state.aiModel} onChange={(m) => this.setState({ aiModel: m })} />
        </div>
      </div>

      {aiError && <div className='alert alert-danger mt-2'>{aiError}</div>}
      {aiResponse && this.renderAiResponse(aiResponse)}


      <h4 className='mt-3'>Top /16 prefixes</h4>
      <table className='table table-sm table-bordered small w-auto'>
        <thead><tr><th>prefix</th><th>country</th><th>total</th><th>429</th><th>403</th></tr></thead>
        <tbody>
          {prefixes.slice(0, drilledPrefixes.length * 2).map(r => {
            const cellProps = prefixCellProps(r.prefix);
            return <tr key={r.prefix}>
              <td {...cellProps} style={styleFromIp(r.prefix, 2)}>{prefix2ToWildcard(r.prefix)}</td>
              <td className='text-center monospace text-nowrap'><CountryLabel code={countryByPrefix[r.prefix]} /></td>
              <td className='text-right' style={{ minWidth: '100px' }}>{r.total}</td>
              <td className='text-right text-warning' style={{ minWidth: '100px' }}>{r.status429}</td>
              <td className='text-right text-danger' style={{ minWidth: '100px' }}>{r.status403}</td>
            </tr>;
          })}
        </tbody>
      </table>

      <h4 className='mt-3'>Top global IPs</h4>
      <table className='table table-sm table-bordered small w-auto'>
        <thead><tr><th>IP</th><th>int</th><th>total</th><th>429</th><th>403</th><th>PTR</th></tr></thead>
        <tbody>
          {globalIps.map(r => {
            const cellProps = ipCellProps(r.ipInt);
            return <tr key={r.ipInt}>
              <td {...cellProps} style={styleFromIp(r.ipInt, 4)}>{r.ip}</td>
              <td className='monospace text-muted'>{r.ipInt}</td>
              <td className='text-right' style={{ minWidth: '100px' }}>{r.total}</td>
              <td className='text-right text-warning' style={{ minWidth: '100px' }}>{r.status429}</td>
              <td className='text-right text-danger' style={{ minWidth: '100px' }}>{r.status403}</td>
              <td className='monospace small'>{ptrByIp[r.ip]}</td>
            </tr>;
          })}
        </tbody>
      </table>

      <h4 className='mt-3'>Drilled IPs by /16 prefix</h4>
      {drilledPrefixes.map(({ prefix, total, status429, status403 }) => {
        const headerCellProps = prefixCellProps(prefix);
        return <div key={prefix} className='mb-2'>
          <div className='small'>
            <b {...headerCellProps}>{prefix2ToWildcard(prefix)}</b>{countryByPrefix[prefix] && <> <span className='badge badge-light border monospace'><CountryLabel code={countryByPrefix[prefix]} /></span></>} total={total} <span className='text-warning'>429={status429}</span> <span className='text-danger'>403={status403}</span>
          </div>
          <table className='table table-sm table-bordered small mb-1 w-auto'>
            <thead><tr><th>IP</th><th>int</th><th>total</th><th>429</th><th>403</th><th>PTR</th></tr></thead>
            <tbody>
              {(ipsByPrefix[prefix] || []).map(r => {
                const cellProps = ipCellProps(r.ipInt);
                return <tr key={r.ipInt}>
                  <td {...cellProps} style={styleFromIp(r.ipInt, 4)}>{r.ip}</td>
                  <td className='monospace text-muted'>{r.ipInt}</td>
                  <td className='text-right' style={{ minWidth: '100px' }}>{r.total}</td>
                  <td className='text-right text-warning' style={{ minWidth: '100px' }}>{r.status429}</td>
                  <td className='text-right text-danger' style={{ minWidth: '100px' }}>{r.status403}</td>
                  <td className='monospace small'>{ptrByIp[r.ip]}</td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>;
      })}

      <h4 className='mt-3'>Known-good crawler ranges</h4>
      <ul className='small'>
        {_.map(knownGoodBotRanges, (info, name) => <li key={name}>
          <b>{name}</b>: {info.error ? <span className='text-danger'>ERROR {info.error}</span> : <>
            {info.count} prefixes (sample: <span className='monospace'>{info.sample.join(', ')}</span>)
          </>}
        </li>)}
      </ul>

    </div>;
  }

  renderAiResponse(aiResponse) {
    const { message, usage, responseTime } = aiResponse;
    let parsed = null;
    try { parsed = JSON.parse(message); } catch (_e) { /* not JSON */ }
    if (parsed && (typeof parsed !== 'object' || Array.isArray(parsed))) parsed = null;

    const meta = <div className='small text-muted'>
      {usage && <>tokens: {usage.total_tokens || (usage.input_tokens + usage.output_tokens)} • </>}
      {responseTime != null && <>response in {responseTime}ms</>}
    </div>;

    const frameProps = {
      className: 'mt-3 p-3 border rounded',
      style: { backgroundColor: '#f2f8fd', borderColor: '#c7dff0', maxWidth: '1200px' }
    };

    if (!parsed) {
      return <div {...frameProps}>
        <h4>AI recommendation</h4>
        {meta}
        <pre className='bg-light border p-2 small' style={{ whiteSpace: 'pre-wrap' }}>{message}</pre>
      </div>;
    }

    const { result } = this.state;
    const parsedKnownGood = buildKnownGoodIndex(result && result.knownGoodCidrs);
    const addBlacklistedIps = parsed.addBlacklistedIps || [];
    const addRestrictedIpRanges = parsed.addRestrictedIpRanges || [];
    const ignored = parsed.ignored || [];
    const totalSuggestions = addBlacklistedIps.length + addRestrictedIpRanges.length;
    const locale = (result && result.locale) || this.state.locale;
    const applyUrl = buildServerApplyUrl(locale, addBlacklistedIps, addRestrictedIpRanges);

    const knownGoodWarning = (m) => m &&
      <span className='text-danger ml-1' title={`Overlaps known-good ${m.name} (${m.cidr}) — do NOT apply`}>⚠ {m.name}</span>;

    return <div {...frameProps}>
      <h4>AI recommendation</h4>
      {meta}

      {parsed.summary && <div className='alert alert-secondary mt-2 mb-2' style={{ maxWidth: '900px' }}>{parsed.summary}</div>}

      <div className='d-flex flex-wrap align-items-start' style={{ gap: '2rem' }}>
        {addBlacklistedIps.length > 0 && <div>
          <h6 className='mb-1'>Blacklist <span className='badge badge-danger'>{addBlacklistedIps.length}</span> <span className='small text-muted'>(hard 403)</span></h6>
          <table className='table table-sm table-bordered small w-auto mb-1 bg-white'>
            <thead><tr><th>IP</th><th>Description</th></tr></thead>
            <tbody>
              {addBlacklistedIps.map((e, i) => <tr key={i}>
                <td className='monospace table-danger'>{e.ip}{knownGoodWarning(matchIpRangeStr(parsedKnownGood, e.ip, e.ip))}</td>
                <td>{e.description}</td>
              </tr>)}
            </tbody>
          </table>
        </div>}

        {addRestrictedIpRanges.length > 0 && <div>
          <h6 className='mb-1'>Throttle ranges <span className='badge badge-warning'>{addRestrictedIpRanges.length}</span> <span className='small text-muted'>(rate-limited 429)</span></h6>
          <table className='table table-sm table-bordered small w-auto mb-1 bg-white'>
            <thead><tr><th>From</th><th>To</th><th>Description</th></tr></thead>
            <tbody>
              {addRestrictedIpRanges.map((e, i) => <tr key={i}>
                <td className='monospace table-warning'>{e.ipFrom}</td>
                <td className='monospace table-warning'>{e.ipTo}{knownGoodWarning(matchIpRangeStr(parsedKnownGood, e.ipFrom, e.ipTo))}</td>
                <td>{e.description}</td>
              </tr>)}
            </tbody>
          </table>
        </div>}

        {ignored.length > 0 && <div className='text-muted'>
          <h6 className='mb-1'>Ignored <span className='badge badge-secondary'>{ignored.length}</span></h6>
          <table className='table table-sm table-bordered small w-auto mb-1 bg-white'>
            <thead><tr><th>What</th><th>Reason</th></tr></thead>
            <tbody>
              {ignored.map((e, i) => <tr key={i}>
                <td className='monospace'>{e.what}</td>
                <td>{e.reason}</td>
              </tr>)}
            </tbody>
          </table>
        </div>}
      </div>

      {totalSuggestions === 0 && <div className='alert alert-success mt-2' style={{ maxWidth: '900px' }}>No new blocklist entries suggested.</div>}

      {totalSuggestions > 0 && <div className='mt-2'>
        <a className='btn btn-success btn-lg' href={applyUrl} target='_blank' rel='noopener noreferrer'>
          🚀 Apply {totalSuggestions} in {SITE_DOMAIN_BY_LOCALE[locale] || 'server'} →
        </a>
        <div className='small text-muted mt-1'>Opens the server IP-blacklist admin with these entries pre-loaded as drafts — review and Save there.</div>
      </div>}

      <details className='mt-3'>
        <summary className='small text-muted' style={{ cursor: 'pointer' }}>Raw response</summary>
        <pre className='bg-light border p-2 small' style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(parsed, null, 2)}</pre>
      </details>
    </div>;
  }
}

export default SecurityTrafficAnalysis;

// For Development purposes
// export default function Component(props) {
//   return <SecurityTrafficAnalysis {...props} />;
// }